<template>
    <main id="js-page-content" class="ftd-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/form-types" class="tbv2-crumb">{{ $t('form_types.title') }}</router-link>
                <span v-if="formType" class="tbv2-sep">/</span>
                <span v-if="formType" class="tbv2-current">{{ formType.name }}</span>
            </div>
        </portal>

        <div v-if="loading" class="ftd-state">
            <i class="fal fa-spinner-third fa-spin"></i>
        </div>

        <template v-else-if="formType">
            <header class="ftd-hero">
                <router-link to="/form-types" class="ftd-back">
                    <i class="fal fa-arrow-left"></i>
                    <span>{{ $t('common.back') || 'Retour' }}</span>
                </router-link>
                <div class="ftd-hero-row">
                    <div class="ftd-hero-icon">
                        <i class="fal fa-layer-group"></i>
                    </div>
                    <div class="ftd-hero-body">
                        <h1 class="ftd-hero-title">{{ formType.name }}</h1>
                        <div class="ftd-pills">
                            <span class="ftd-pill ftd-pill--id">#{{ formType.id }}</span>
                            <span class="ftd-pill" :class="formType.active ? 'ftd-pill--yes' : 'ftd-pill--no'">
                                {{ formType.active ? $t('forms.status.active') : $t('forms.parametrage.inactive') }}
                            </span>
                            <span class="ftd-pill" :class="purposeClass(formType.purpose)">
                                {{ purposeLabel(formType.purpose) }}
                            </span>
                            <span v-if="formType.listable" class="ftd-pill ftd-pill--info">
                                <i class="fal fa-list"></i>
                                {{ $t('form_types.listable') }}
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            <section
                v-if="formType.translations && formType.translations.length"
                class="ftd-card"
            >
                <FormTabHeader
                    icon="fal fa-language"
                    :title="$t('form_types.description')"
                />
                <div class="ftd-body ftd-translations">
                    <div
                        v-for="translation in formType.translations"
                        :key="translation.locale"
                        class="ftd-translation"
                    >
                        <span class="ftd-lang-badge">{{ translation.locale }}</span>
                        <div class="ftd-translation-body">
                            <span class="ftd-translation-name">{{ translation.name }}</span>
                            <p v-if="translation.description" class="ftd-translation-desc">
                                {{ translation.description }}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section v-if="formType.statuses && formType.statuses.length" class="ftd-card">
                <FormTabHeader
                    icon="fal fa-tasks"
                    :title="$t('forms.menu.status')"
                    :subtitle="statusesSubtitle"
                />
                <div class="ftd-body ftd-statuses">
                    <span
                        v-for="status in formType.statuses"
                        :key="status.id"
                        class="ftd-status"
                        :class="{ 'ftd-status--inactive': !status.active }"
                    >
                        <i v-if="status.icon" :class="status.icon"></i>
                        {{ status.name }}
                    </span>
                </div>
            </section>

            <section class="ftd-card">
                <FormTabHeader
                    icon="fal fa-file-alt"
                    :title="$t('form_types.associated_forms')"
                    :subtitle="formsSubtitle"
                />
                <div v-if="forms.length === 0" class="ftd-empty">
                    <i class="fal fa-poll"></i>
                    <p>{{ $t('form_types.no_forms') }}</p>
                </div>
                <div v-else class="ftd-table-wrapper">
                    <table class="ftd-table">
                        <thead>
                            <tr>
                                <th class="ftd-col-narrow">ID</th>
                                <th>{{ $t('forms.form') }}</th>
                                <th class="ftd-col-center">{{ $t('forms.steps') }}</th>
                                <th class="ftd-col-center">{{ $t('forms.attributes') }}</th>
                                <th class="ftd-col-center">{{ $t('forms.tickets') }}</th>
                                <th class="ftd-col-actions"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="form in forms" :key="form.id">
                                <td class="ftd-cell-id">#{{ form.id }}</td>
                                <td class="ftd-cell-name">{{ form.name }}</td>
                                <td class="ftd-cell-num">{{ form.steps_count }}</td>
                                <td class="ftd-cell-num">{{ form.attributes_count }}</td>
                                <td class="ftd-cell-num">
                                    <router-link
                                        :to="`/posts?form_id=${form.id}`"
                                        class="ftd-tickets-link"
                                    >
                                        {{ form.posts_count }}
                                    </router-link>
                                </td>
                                <td class="ftd-cell-actions">
                                    <router-link :to="`/forms/${form.id}`" class="ftd-btn" :title="$t('form_types.view')">
                                        <i class="fal fa-eye"></i>
                                    </router-link>
                                    <router-link :to="`/forms/${form.id}/edit`" class="ftd-btn" :title="$t('common.edit')">
                                        <i class="fal fa-pen"></i>
                                    </router-link>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </template>
    </main>
</template>

<script>
import { mapActions } from 'vuex'
import FormTabHeader from '@/components/Model/Form/FormComponents/FormTabHeader'

export default {
    name: 'FormTypeDetail',
    components: { FormTabHeader },
    data() {
        return {
            formType: null,
            forms: [],
            loading: true
        }
    },
    computed: {
        statusesSubtitle() {
            const n = this.formType && this.formType.statuses ? this.formType.statuses.length : 0
            return n === 1 ? `${n} statut` : `${n} statuts`
        },
        formsSubtitle() {
            const n = this.forms.length
            if (n === 0) return this.$t('form_types.no_forms')
            return n === 1 ? `${n} formulaire` : `${n} formulaires`
        }
    },
    created() {
        this.loadFormType()
    },
    methods: {
        ...mapActions('form', ['getFormType']),
        purposeLabel(purpose) {
            const key = `form_types.purposes.${purpose || 'other'}`
            const translated = this.$t(key)
            return translated !== key ? translated : purpose
        },
        purposeClass(purpose) {
            const map = {
                ticket: 'ftd-pill--cyan',
                survey: 'ftd-pill--purple',
                other: 'ftd-pill--neutral'
            }
            return map[purpose] || 'ftd-pill--amber'
        },
        loadFormType() {
            this.loading = true
            this.getFormType(this.$route.params.id)
                .then((response) => {
                    const data = response.data.type || response.data
                    this.formType = data
                    this.forms = data.forms || []
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
                .finally(() => {
                    this.loading = false
                })
        }
    }
}
</script>

<style scoped>
.ftd-v2 {
    width: 100%;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover {
    color: var(--v2-primary);
}

.tbv2-sep {
    color: var(--v2-text-4);
}

.tbv2-current {
    color: var(--v2-text-1);
}

.ftd-state {
    padding: var(--v2-space-6);
    display: flex;
    justify-content: center;
    color: var(--v2-text-3);
    font-size: 1.25rem;
}

.ftd-hero {
    margin-bottom: var(--v2-space-5);
}

.ftd-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 12px;
    transition: color var(--v2-transition-fast);
}

.ftd-back:hover {
    color: var(--v2-primary);
}

.ftd-back i {
    font-size: 0.75rem;
}

.ftd-hero-row {
    display: flex;
    align-items: center;
    gap: 16px;
}

.ftd-hero-icon {
    width: 48px;
    height: 48px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.35rem;
    flex-shrink: 0;
}

.ftd-hero-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.ftd-hero-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.2;
}

.ftd-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.ftd-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: var(--v2-font-weight-semibold);
    white-space: nowrap;
}

.ftd-pill i {
    font-size: 0.7rem;
}

.ftd-pill--id {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
    font-variant-numeric: tabular-nums;
}

.ftd-pill--yes { background: rgba(16, 185, 129, 0.12); color: #059669; }
.ftd-pill--no { background: rgba(239, 68, 68, 0.12); color: #dc2626; }
.ftd-pill--cyan { background: rgba(8, 145, 178, 0.12); color: #0891B2; }
.ftd-pill--purple { background: rgba(139, 92, 246, 0.12); color: #7c3aed; }
.ftd-pill--neutral { background: var(--v2-surface-2); color: var(--v2-text-3); }
.ftd-pill--amber { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.ftd-pill--info { background: rgba(59, 130, 246, 0.12); color: #2563eb; }

.ftd-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
    margin-bottom: var(--v2-space-4);
}

.ftd-card:last-child {
    margin-bottom: 0;
}

.ftd-body {
    padding: var(--v2-space-4) var(--v2-space-5);
}

/* Translations */
.ftd-translations {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ftd-translation {
    display: flex;
    align-items: flex-start;
    gap: 12px;
}

.ftd-lang-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: 0.66rem;
    font-weight: var(--v2-font-weight-bold);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    flex-shrink: 0;
    margin-top: 2px;
}

.ftd-translation-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.ftd-translation-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.ftd-translation-desc {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    line-height: 1.4;
}

/* Statuses */
.ftd-statuses {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.ftd-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 999px;
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
}

.ftd-status i {
    font-size: 0.8rem;
}

.ftd-status--inactive {
    background: var(--v2-surface-2);
    color: var(--v2-text-3);
}

/* Empty */
.ftd-empty {
    padding: var(--v2-space-6);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    color: var(--v2-text-4);
}

.ftd-empty i {
    font-size: 2rem;
}

.ftd-empty p {
    margin: 0;
    font-size: var(--v2-text-sm-size);
}

/* Table */
.ftd-table-wrapper {
    overflow-x: auto;
}

.ftd-table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--v2-text-sm-size);
}

.ftd-table th {
    text-align: left;
    padding: 10px 16px;
    background: var(--v2-surface-1);
    color: var(--v2-text-3);
    font-weight: var(--v2-font-weight-semibold);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-size: 0.68rem;
    border-bottom: 1px solid var(--v2-border);
    white-space: nowrap;
}

.ftd-table td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--v2-border-subtle);
    color: var(--v2-text-2);
    vertical-align: middle;
}

.ftd-table tr:last-child td {
    border-bottom: none;
}

.ftd-col-narrow { width: 60px; }
.ftd-col-center { text-align: center; width: 110px; }
.ftd-col-actions { width: 100px; text-align: right; }

.ftd-cell-id {
    color: var(--v2-text-4);
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    font-weight: var(--v2-font-weight-semibold);
}

.ftd-cell-name {
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.ftd-cell-num {
    text-align: center;
    font-variant-numeric: tabular-nums;
    color: var(--v2-text-2);
}

.ftd-tickets-link {
    color: var(--v2-primary);
    font-weight: var(--v2-font-weight-semibold);
    text-decoration: none !important;
    transition: color var(--v2-transition-fast);
}

.ftd-tickets-link:hover {
    color: var(--v2-primary-hover, #0e7490);
    text-decoration: underline !important;
}

.ftd-cell-actions {
    text-align: right;
    white-space: nowrap;
}

.ftd-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--v2-radius-sm);
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    color: var(--v2-text-2);
    text-decoration: none !important;
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    margin-left: 4px;
}

.ftd-btn i {
    font-size: 0.75rem;
}

.ftd-btn:hover {
    border-color: var(--v2-primary);
    color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

@media (max-width: 900px) {
    .ftd-v2 {
        padding: 16px 18px 32px;
    }

    .ftd-hero-row {
        gap: 12px;
    }

    .ftd-hero-title {
        font-size: 1.25rem;
    }

    .ftd-body {
        padding: var(--v2-space-3) var(--v2-space-4);
    }

    .ftd-table th,
    .ftd-table td {
        padding: 10px 12px;
    }
}
</style>
